import moment from 'moment';
import firebase from '../config/firebase.config';
import map from 'lodash/map';
import { reject } from 'lodash';
import groupBy from 'lodash/groupBy';
import { getTeamMemberDetails } from './teamMemberDetails';
import {
  readShardSnapshotsInTransaction,
  resolveMemberRosterLocation,
  buildRosterPatchWrite,
} from './teamMemberDetailsRosterPatch';
import httpClient from '@/config/httpClient';
const database = firebase.firestore();

/**
 * Build the teamList document payload for an Admin (same shape as createTeamWithMembers).
 * @param {string} teamName
 * @param {string} teamId
 * @param {string} email
 * @returns {{ Role: string, Team: string, activeTeamid: string, email: string }}
 */
function buildTeamListDocForAdmin(teamName, teamId, email) {
  return {
    Role: 'Admin',
    Team: teamName,
    activeTeamid: teamId,
    email,
  };
}

/**
 * Build the memberDetails entry for an Admin (same shape as createTeamWithMembers).
 * @param {string} email
 * @param {string} name
 * @returns {{ userEmailid: string, userName: string, userType: string }}
 */
function buildMemberDetailForAdmin(email, name) {
  return {
    userEmailid: email,
    userName: name,
    userType: 'Admin',
  };
}

/**
 * Gets all team members from all teams
 * @param {*} teams
 * @returns
 */
async function getAllTeamMembersData(payload) {
  let { teams, filterBy } = payload;
  try {
    let tempTeams = [...teams];
    let finalizeTeamMembers = [];

    let count = 0;
    do {
      let teamId = tempTeams[count].activeTeamid;
      let teamName = tempTeams[count].Team;
      const memberDetails = await getTeamMemberDetails(database, teamId);
      const data = Object.entries(memberDetails)
        .filter(([, m]) => m && m.deleted !== true)
        .map(([userUID, value]) => ({
          ...value,
          userUID,
          role: value.userType,
        }));
      if (data.length > 0) {
        map(data, (member) => {
          finalizeTeamMembers.push({ teamId, teamName, ...member });
        });
      }
      count = count + 1;
    } while (count < tempTeams.length);

    let groupedData = groupBy(finalizeTeamMembers, 'userEmailid');

    let allMembers = [];

    allMembers = map(groupedData, (group, index) => {
      let obj = {};
      obj['userUID'] = group[0].userUID;
      obj['userEmail'] = group[0].userEmailid;
      obj['userName'] = group[0].userName;
      obj['role'] = group[0].role;
      obj['rates'] = group[0].rates;
      // Helper function to deduplicate teams based on teamId
      const deduplicateTeams = (teams) => {
        const seen = new Set();
        return teams.filter((team) => {
          const duplicate = seen.has(team.teamId);
          seen.add(team.teamId);
          return !duplicate;
        });
      };

      if (!filterBy) {
        obj['teams'] = deduplicateTeams(
          group.map((fnlItm) => ({
            teamId: fnlItm.teamId,
            teamName: fnlItm.teamName,
            role: fnlItm.userType,
          }))
        );
      } else {
        let preparedTms = [];
        if (filterBy?.teams && filterBy?.role) {
          let isMember = group.filter((itm) => itm.userType == filterBy?.role);
          preparedTms = deduplicateTeams(
            isMember
              .filter((itm) => filterBy?.teams.includes(itm.teamId))
              .map((fnlItm) => ({
                teamId: fnlItm.teamId,
                teamName: fnlItm.teamName,
                role: fnlItm.userType,
              }))
          );
        } else if (filterBy?.teams) {
          preparedTms = deduplicateTeams(
            group
              .filter((itm) => filterBy?.teams.includes(itm.teamId))
              .map((fnlItm) => ({
                teamId: fnlItm.teamId,
                teamName: fnlItm.teamName,
                role: fnlItm.userType,
              }))
          );
        } else if (filterBy?.role) {
          preparedTms = deduplicateTeams(
            group
              .filter((itm) => itm.userType == filterBy?.role)
              .map((fnItm) => ({
                teamId: fnItm.teamId,
                teamName: fnItm.teamName,
                role: fnItm.userType,
              }))
          );
        }
        obj['teams'] = preparedTms;
      }
      return { ...obj };
    });
    if (filterBy) {
      let temp = allMembers.filter((fm) => fm.teams.length > 0);
      allMembers = [...temp];
    }
    return { success: true, data: [...allMembers] };
  } catch (e) {
    return { success: false, data: e };
  }
}

/**
 * Create new team with or without members
 * @param {*} payload
 * @returns
 */
async function createTeamWithMembers(payload) {
  try {
    const { teamName, ownedBy, createdBy, members = [] } = payload;

    const cityRef = database.collection('companyDatabase').doc(ownedBy);
    const doc = await cityRef.get();
    const companyTeam = doc.data();

    let teamDBRef = database.collection('teamDatabase').doc(),
      memberDBRef = database
        .collection('membersDatabase')
        .doc(createdBy.userId),
      memberCompanyListRef = memberDBRef
        .collection('companyList')
        .doc(ownedBy)
        .collection('teamList')
        .doc(teamDBRef.id),
      // ownerDBRef = memberDBRef.collection('teamList').doc(teamDBRef.id),
      companyTeamsRef = database
        .collection('companyDatabase')
        .doc(ownedBy)
        .collection('teams')
        .doc(teamDBRef.id);
    let companyDBRef = database.collection('companyDatabase').doc(ownedBy);

    let batch = database.batch(),
      memberDetails = {},
      membersIDs = [];
    // 1. Extract admins from the company data
    const adminUserIds = companyTeam.admins || [];

    // 2. For each admin, fetch member details and add to memberDetails + member's teamList
    for (const adminId of adminUserIds) {
      const adminDoc = await database
        .collection('membersDatabase')
        .doc(adminId)
        .get();
      if (adminDoc.exists) {
        const adminData = adminDoc.data();
        const email = adminData.accountEmailid;
        const name = adminData.accountFullName;

        memberDetails[adminId] = buildMemberDetailForAdmin(email, name);
        membersIDs.push(adminId);

        const adminTeamListRef = database
          .collection('membersDatabase')
          .doc(adminId)
          .collection('companyList')
          .doc(ownedBy)
          .collection('teamList')
          .doc(teamDBRef.id);
        batch.set(
          adminTeamListRef,
          buildTeamListDocForAdmin(teamName, teamDBRef.id, email),
          { merge: true }
        );
      }
    }

    membersIDs = members.map((i) => i.userUID);

    const data = {
      teamId: teamDBRef.id,
      members: membersIDs,
    };

    // Sets a creator as a default member
    memberDetails[createdBy.userId] = {
      userEmailid: createdBy.email,
      userName: createdBy.name,
      userType: createdBy.role,
    };

    members.map((mem) => {
      memberDetails[mem.userUID] = {
        userEmailid: mem.userEmail,
        userName: mem.userName,
        userType: mem?.role,
      };

      // ** Adds team details to individual member's account relevant to company
      let memTeamListRef = database
        .collection('membersDatabase')
        .doc(mem.userUID)
        .collection('companyList')
        .doc(ownedBy)
        .collection('teamList')
        .doc(teamDBRef.id);
      batch.set(
        memTeamListRef,
        {
          Tasks: {},
          Role: mem?.role,
          ...(mem?.role !== 'Admin' ? { teamRole: mem?.role } : {}),
          Team: teamName,
          completedTasks: {},
          email: mem.userEmail,
        },
        { merge: true }
      );
    });

    // ** Adds to the teamList under the companyList in CompanyDatabase
    batch.set(
      memberCompanyListRef,
      {
        Role: createdBy.role,
        Team: teamName,
        email: createdBy.email,
        activeTeamid: teamDBRef.id,
      },
      { merge: true }
    );

    // ** Adds to teamDb with memberDetails in teamDatabase
    batch.set(
      teamDBRef,
      {
        teamName: teamName,
        ownedBy: ownedBy,
        createdBy: createdBy.userId,
        memberDetails: { ...memberDetails },
      },
      { merge: true }
    );

    // ** Updates to the companyDatabase
    batch.set(
      companyTeamsRef,
      {
        teamId: teamDBRef.id,
        // members: membersIDs.length > 0 ? membersIDs : {},
        ...(membersIDs.length > 0 ? { members: membersIDs } : {}),
      },
      { merge: true }
    );

    if (companyTeam.teams) {
      const newData = companyTeam.teams;
      newData.push(data);
      batch.update(companyDBRef, {
        teams: firebase.firestore.FieldValue.arrayUnion(teamDBRef.id),
      });
    } else {
      batch.update(companyDBRef, {
        teams: [teamDBRef.id],
      });
    }

    return batch.commit().then(() => {
      return { success: true, data: 'Department created successfully!' };
    });
  } catch (e) {
    return { success: false, data: e };
  }
}

/**
 * Edit team details with members
 * @param {*} payload
 * @returns
 */
async function editTeamWithMembers(payload) {
  // Authorized and written server-side (Admin only) — see
  // functions/employee-team-access-functions.js's editTeam.
  try {
    const user = firebase.auth().currentUser;
    if (!user) throw new Error('User not authenticated');
    const idToken = await user.getIdToken();
    const response = await httpClient.post('/editTeam', payload, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!response?.data?.success) {
      return {
        success: false,
        data: response?.data?.message || 'Failed to update department',
      };
    }
    return { success: true, data: 'Department updated successfully!' };
  } catch (e) {
    return {
      success: false,
      data:
        e?.response?.data?.message ||
        e?.message ||
        'Failed to update department',
    };
  }
}

const VALID_ROLES = ['Member', 'Manager', 'Admin'];

/**
 * Updates employee details (partial update: only provided fields are updated).
 * - name: updates membersDatabase.accountFullName and teamDatabase memberDetails.{userId}.userName
 *   on every team in this company where the user has a non-deleted teamList entry
 * - deskId: delegated to the updateEmployeeDeskId Cloud Function (verifies license exists,
 *   resolves conflicts, sets membersDatabase.encryptedLicenseKey)
 * - timeZone: updates membersDatabase.timeZone only (not team roster)
 * - role: delegates to the updateEmployeeRole Cloud Function (server-authorized)
 * @param {Object} payload - { userId, companyId, teamId, name?, deskId?, timeZone?, role? }
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
async function updateEmployeeDetails(payload) {
  console.log('[updateEmployeeDetails] payload', payload);
  const { userId, companyId, teamId, deskId, timeZone, role, name } = payload;
  if (!userId) {
    return { success: false, message: 'userId is required' };
  }
  if (name !== undefined && name !== null && String(name).trim() === '') {
    return { success: false, message: 'Name cannot be empty' };
  }
  const hasDeskId =
    deskId !== undefined && deskId !== null && String(deskId).trim() !== '';
  const hasTimeZone =
    timeZone !== undefined &&
    timeZone !== null &&
    String(timeZone).trim() !== '';
  const hasRole =
    role !== undefined && role !== null && VALID_ROLES.includes(role);
  const hasName =
    name !== undefined && name !== null && String(name).trim() !== '';
  console.log(
    '[updateEmployeeDetails] hasDeskId',
    hasDeskId,
    'hasTimeZone',
    hasTimeZone,
    'hasRole',
    hasRole,
    'hasName',
    hasName
  );
  if (!hasDeskId && !hasTimeZone && !hasRole && !hasName) {
    return {
      success: false,
      message: 'At least one of name, deskId, timeZone, or role is required',
    };
  }
  if (hasRole && !VALID_ROLES.includes(role)) {
    return {
      success: false,
      message: `role must be one of: ${VALID_ROLES.join(', ')}`,
    };
  }
  if ((hasTimeZone || hasRole || hasName) && (!companyId || !teamId)) {
    return {
      success: false,
      message:
        'companyId and teamId are required for name, timeZone, or role update',
    };
  }

  // Role changes are authorized and written server-side (only a Super Admin
  // may grant Admin) — see functions/employee-team-access-functions.js's
  // updateEmployeeRole. Everything else below stays a direct client write.
  if (hasRole) {
    try {
      const updateEmployeeRole = firebase
        .functions()
        .httpsCallable('updateEmployeeRole');
      await updateEmployeeRole({ userId, companyId, teamId, role });
    } catch (e) {
      return {
        success: false,
        message: e.message || 'Failed to update role',
      };
    }
  }

  // Desk ID assignment looks up the license and reassigns it from any other
  // holder — that needs Firestore queries inside a transaction, which the web
  // SDK doesn't support, so it runs server-side (updateEmployeeDeskId). It runs
  // before the timezone/name transaction so a license error aborts the rest,
  // as it did when everything shared one transaction.
  if (hasDeskId) {
    if (!companyId) {
      return {
        success: false,
        message: 'companyId is required for deskId update',
      };
    }
    try {
      const updateEmployeeDeskId = firebase
        .functions()
        .httpsCallable('updateEmployeeDeskId');
      await updateEmployeeDeskId({
        userId,
        companyId,
        deskId: String(deskId).trim(),
      });
    } catch (e) {
      return {
        success: false,
        message: e.message || 'Failed to update desk ID',
      };
    }
  }

  try {
    const needsRoster = hasTimeZone || hasName;
    const memberRef = database.collection('membersDatabase').doc(userId);

    let teamIdsForUserInCompany = [];
    if (needsRoster && hasName) {
      const teamListSnap = await memberRef
        .collection('companyList')
        .doc(companyId)
        .collection('teamList')
        .get();
      teamIdsForUserInCompany = teamListSnap.docs
        .filter((d) => (d.data() || {}).deleted !== true)
        .map((d) => d.id);
    }

    const memberPatch = {};
    if (hasTimeZone) {
      memberPatch.timeZone = timeZone.trim();
    }
    if (hasName) {
      memberPatch.accountFullName = String(name).trim();
    }

    const trimmedDisplayName = hasName ? String(name).trim() : '';
    const teamIdsForMemberDetails =
      needsRoster && hasName
        ? [...new Set(teamIdsForUserInCompany).add(teamId)]
        : [];

    if (needsRoster) {
      console.log('[updateEmployeeDetails] atomic transaction', {
        needsRoster,
        hasTimeZone,
        hasRole,
        hasName,
        teamId,
        companyId,
      });

      const teamDb = database.collection('teamDatabase');

      await database.runTransaction(async (transaction) => {
        await transaction.get(memberRef);

        const teamReadContext = new Map();
        if (needsRoster && teamIdsForMemberDetails.length > 0) {
          for (const tid of teamIdsForMemberDetails) {
            const tRef = teamDb.doc(tid);
            const teamSnap = await transaction.get(tRef);
            const shardSnapshots = await readShardSnapshotsInTransaction(
              transaction,
              tRef,
              teamSnap.data() || {}
            );
            teamReadContext.set(tid, { teamSnap, shardSnapshots });
          }
        }

        if (Object.keys(memberPatch).length > 0) {
          transaction.update(memberRef, memberPatch);
        }

        if (needsRoster) {
          for (const tid of teamIdsForMemberDetails) {
            const { teamSnap, shardSnapshots } = teamReadContext.get(tid);
            const location = resolveMemberRosterLocation(
              teamSnap.data() || {},
              shardSnapshots,
              userId
            );
            if (location.type === 'none') {
              throw new Error(
                `Member is not on this team's roster (team ${tid}). Try refreshing the page.`
              );
            }

            /** @type {Partial<{ userName: string }>} */
            const rosterFields = {};
            if (hasName) rosterFields.userName = trimmedDisplayName;

            if (rosterFields.userName === undefined) continue;

            const { ref, data } = buildRosterPatchWrite(
              teamDb.doc(tid),
              location,
              userId,
              rosterFields
            );
            transaction.update(ref, data);
          }
        }
      });

      console.log('[updateEmployeeDetails] transaction completed');
    }
    console.log('[updateEmployeeDetails] success');
    return { success: true };
  } catch (e) {
    console.error('[updateEmployeeDetails] error', e, 'payload', payload);
    return {
      success: false,
      message: e.message || 'Failed to update employee details',
    };
  }
}

/**
 * Soft-remove user from team and revoke license: set deleted/deletedAt on team and teamList,
 * clear encryptedLicenseKey. Used from Employee List.
 * When removing from the user's active team (in the active company), we must ensure the user
 * still has at least one team and we set a new activeTeamId.
 * @param {Object} payload - { userId, companyId, teamId, newActiveTeamId?: string }
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
async function removeUsers(payload) {
  const { userId, companyId, teamId, newActiveTeamId } = payload;
  if (!userId || !companyId || !teamId) {
    return {
      success: false,
      message: 'userId, companyId and teamId are required',
    };
  }
  // Authorized and written server-side (Admin, or a Manager scoped to teams
  // they manage) — see functions/employee-team-access-functions.js's
  // removeTeamMember.
  try {
    const user = firebase.auth().currentUser;
    if (!user) throw new Error('User not authenticated');
    const idToken = await user.getIdToken();
    const response = await httpClient.post(
      '/removeTeamMember',
      { userId, companyId, teamId, newActiveTeamId },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    if (!response?.data?.success) {
      return {
        success: false,
        message:
          response?.data?.message || 'Failed to remove user from department',
      };
    }
    return { success: true };
  } catch (e) {
    console.error('removeUsers error:', e);
    return {
      success: false,
      message:
        e?.response?.data?.message ||
        e?.message ||
        'Failed to remove user from department',
    };
  }
}

/**
 * Move multiple employees from one team to another via the bulkMoveTeamMembers
 * Cloud Function (server-side, per-employee transactional).
 * @param {Object} payload - { companyId, sourceTeamId, destinationTeamId, employeeIds, updateActiveTeamForAll }
 * @returns {Promise<{ success: boolean, message?: string, data?: { moved: Array, errors: Array, stats: Object } }>}
 */
async function bulkMoveTeamMembers(payload) {
  try {
    const user = firebase.auth().currentUser;
    if (!user) throw new Error('User not authenticated');
    const idToken = await user.getIdToken();
    const response = await httpClient.post('/bulkMoveTeamMembers', payload, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!response?.data?.success) {
      return {
        success: false,
        message: response?.data?.message || 'Failed to move employees',
        data: response?.data?.data,
      };
    }
    return {
      success: true,
      message: response.data.message,
      data: response.data.data,
    };
  } catch (e) {
    return {
      success: false,
      message:
        e?.response?.data?.message || e?.message || 'Failed to move employees',
    };
  }
}

/**
 * Soft-remove user from company: marks companyList and all teamList/team memberDetails
 * for that company as deleted, clears license, updates active company/team if needed,
 * and removes user from company admins. Separate from removeUsers (remove from team).
 * @param {Object} payload - { userId, companyId }
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
async function removeUserFromCompany(payload) {
  const { userId, companyId } = payload;
  if (!userId || !companyId) {
    return {
      success: false,
      message: 'userId and companyId are required',
    };
  }
  try {
    const memberRef = database.collection('membersDatabase').doc(userId);
    const companyListRef = memberRef.collection('companyList').doc(companyId);

    const [memberSnap, companyListSnap] = await Promise.all([
      memberRef.get(),
      companyListRef.get(),
    ]);

    if (!memberSnap.exists) {
      return { success: false, message: 'User not found.' };
    }
    if (!companyListSnap.exists) {
      return { success: false, message: 'User is not in this company.' };
    }

    const companyListData = companyListSnap.data() || {};
    if (companyListData.deleted === true) {
      return {
        success: false,
        message: 'User is already removed from this company.',
      };
    }

    const memberData = memberSnap.data() || {};
    const activeCompanyId = memberData.activeCompanyId || null;
    const activeTeamId = memberData.activeTeamId || null;

    const teamListSnap = await companyListRef.collection('teamList').get();
    const teamIds = [];
    teamListSnap.forEach((doc) => {
      const d = doc.data() || {};
      if (d.deleted === true) return;
      teamIds.push(doc.id);
    });

    let newActiveCompanyId = null;
    let newActiveTeamId = null;
    if (activeCompanyId === companyId) {
      const allCompanyListSnap = await memberRef
        .collection('companyList')
        .get();
      for (const doc of allCompanyListSnap.docs) {
        if (doc.id === companyId) continue;
        const data = doc.data() || {};
        if (data.deleted === true) continue;
        newActiveCompanyId = doc.id;
        const teamSnap = await doc.ref.collection('teamList').get();
        const firstTeam = teamSnap.docs.find(
          (t) => (t.data() || {}).deleted !== true
        );
        if (firstTeam) newActiveTeamId = firstTeam.id;
        break;
      }
    }

    const batch = database.batch();

    batch.set(
      companyListRef,
      {
        deleted: true,
        deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
      },
      { merge: true }
    );

    for (const teamId of teamIds) {
      const teamRef = database.collection('teamDatabase').doc(teamId);
      batch.update(teamRef, {
        [`memberDetails.${userId}.deleted`]: true,
        [`memberDetails.${userId}.deletedAt`]:
          firebase.firestore.FieldValue.serverTimestamp(),
      });
      const teamListRef = companyListRef.collection('teamList').doc(teamId);
      batch.set(
        teamListRef,
        {
          deleted: true,
          deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true }
      );
    }

    const memberUpdate = {
      encryptedLicenseKey: null,
    };
    if (activeCompanyId === companyId) {
      memberUpdate.activeCompanyId = newActiveCompanyId;
      memberUpdate.activeTeamId = newActiveTeamId;
    }
    batch.update(memberRef, memberUpdate);

    const companyRef = database.collection('companyDatabase').doc(companyId);
    const companySnap = await companyRef.get();
    if (companySnap.exists) {
      const admins = (companySnap.data() || {}).admins || [];
      if (admins.includes(userId)) {
        batch.update(companyRef, {
          admins: firebase.firestore.FieldValue.arrayRemove(userId),
        });
      }
    }
    const removeSeatResponse = await httpClient.post(
      '/decreaseSubscriptionQuantity',
      {
        companyId,
        quantityToRemove: 1,
      }
    );
    if (removeSeatResponse.data.success) {
      await batch.commit();
      return { success: true };
    }
    return {
      success: false,
      message:
        removeSeatResponse.data.message || 'Failed to remove user from company',
    };
  } catch (e) {
    console.error('removeUserFromCompany error:', e);
    return {
      success: false,
      message: e.message || 'Failed to remove user from company',
    };
  }
}

export default {
  getAllTeamMembersData,
  createTeamWithMembers,
  editTeamWithMembers,
  updateEmployeeDetails,
  removeUsers,
  bulkMoveTeamMembers,
  removeUserFromCompany,
};
